import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useClerk } from '../contexts/TestAuthContext';
import { usePermissions, Permissions, RequirePermission } from '../contexts/PermissionsContext';
import QuotaMeter from '../components/QuotaMeter';
import ReportCard from '../components/ReportCard';
import SavedViewsManager from '../components/SavedViewsManager';
import ReportTemplatesManager from '../components/ReportTemplatesManager';
import NotificationBell from '../components/NotificationBell';
import { useBranding } from '../contexts/BrandingContext';
import { formatCurrency } from '../utils/formatCurrency';
import { useActivityLog } from '../hooks/useActivityLog';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Dashboard() {
  const navigate = useNavigate();
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const { signOut } = useClerk();
  const { branding } = useBranding();
  const { hasPermission, role, roleDisplay, roleColor } = usePermissions();
  const { logActivity } = useActivityLog();
  const [orgData, setOrgData] = useState({ used: 0, limit: 25, tier: 'smb' });
  const [reports, setReports] = useState([]);
  const [healthScores, setHealthScores] = useState({});
  const [collaborationStatus, setCollaborationStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const [inProgressCleanup, setInProgressCleanup] = useState<any>(null);

  // Filter & Sort State
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [healthFilter, setHealthFilter] = useState('all');
  const [collaborationFilter, setCollaborationFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date_desc');

  // Bulk Action State
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedReports, setSelectedReports] = useState(new Set());

  // Tag Filter State
  const [tagFilter, setTagFilter] = useState('');

  // Inline messages (replacing alert() calls)
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Saved Views & Templates handlers
  const handleApplySavedView = (view) => {
    // Apply filters from saved view
    if (view.filters) {
      if (view.filters.status) setStatusFilter(view.filters.status);
      if (view.filters.healthFilter) setHealthFilter(view.filters.healthFilter);
      if (view.filters.collaborationFilter) setCollaborationFilter(view.filters.collaborationFilter);
      if (view.filters.searchQuery) setSearchQuery(view.filters.searchQuery);
      if (view.filters.tagFilter) setTagFilter(view.filters.tagFilter);
    }
    // Apply sort
    if (view.sort_by && view.sort_order) {
      setSortBy(`${view.sort_by}_${view.sort_order}`);
    }
  };

  const handleSelectTemplate = (template) => {
    // Navigate to create report with template data pre-filled
    navigate('/create-report', { state: { templateData: template.template_data } });
  };

  // Bulk Selection Handlers
  const handleSelectReport = (reportId, checked) => {
    setSelectedReports(prev => {
      const newSelected = new Set(prev);
      if (checked) {
        newSelected.add(reportId);
      } else {
        newSelected.delete(reportId);
      }
      return newSelected;
    });
  };

  const handleSelectAll = () => {
    if (selectedReports.size === filteredAndSortedReports.length) {
      setSelectedReports(new Set());
    } else {
      setSelectedReports(new Set(filteredAndSortedReports.map(r => r.id)));
    }
  };

  // Bulk Delete Handler
  const handleBulkDelete = async () => {
    if (selectedReports.size === 0) {
      setErrorMessage('No reports selected');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedReports.size} report(s)?\n\nThis action cannot be undone.`)) {
      return;
    }

    try {
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      
      const results = await Promise.allSettled(
        Array.from(selectedReports).map(reportId =>
          axios.delete(`${BACKEND_URL}/api/v1/reports/${reportId}`, { headers })
        )
      );
      
      const successful = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.length - successful;
      
      if (failed === 0) {
        setStatusMessage(`Successfully deleted ${successful} report(s)`);
      } else {
        setErrorMessage(`Deleted ${successful} report(s), but ${failed} failed.`);
      }
      
      setSelectedReports(new Set());
      setBulkMode(false);
      await loadData();
    } catch (error) {
      console.error('Bulk delete failed:', error);
      setErrorMessage('Failed to delete reports. Please try again.');
    }
  };

  // CSV Export Handler
  const handleExportCSV = () => {
    const reportsToExport = bulkMode && selectedReports.size > 0 
      ? filteredAndSortedReports.filter(r => selectedReports.has(r.id))
      : filteredAndSortedReports;

    if (reportsToExport.length === 0) {
      setErrorMessage('No reports to export');
      return;
    }

    const headers = ['Report ID', 'Client Name', 'Claimant Name', 'Status', 'Claim Amount', 'Created Date'];
    const rows = reportsToExport.map(r => [
      r.id,
      r.client_name || 'N/A',
      r.claimant_name || 'N/A',
      r.status,
      r.result?.final_claim_amount ? `${formatCurrency(Number(r.result.final_claim_amount))}` : '$0',
      new Date(r.created_at).toLocaleDateString()
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `claimgenius_reports_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setStatusMessage(`Exported ${reportsToExport.length} report(s) to CSV`);
  };

  // Check for in-progress cleanup jobs
  const checkInProgressCleanup = async () => {
    try {
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${BACKEND_URL}/api/v1/cleanup/jobs`, { headers });
      const jobs = response.data || [];
      
      // Find jobs that are in progress (not completed)
      const inProgress = jobs.find((job: any) => 
        job.status === 'processing' || 
        job.status === 'validating' ||
        (job.status === 'completed' && job.progress_pct < 100)
      );
      
      if (inProgress) {
        setInProgressCleanup(inProgress);
      }
    } catch (err) {
      // Silently fail - cleanup jobs might not exist
      console.log('No in-progress cleanup jobs');
    }
  };

  const loadData = async () => {
    console.log('🔄 Dashboard: loadData called');
    try {
      const token = await getToken();
      console.log('🔑 Dashboard: Got token:', token ? 'yes' : 'NO TOKEN');

      if (!token) {
        console.error('❌ Dashboard: No auth token available');
        setLoading(false);
        return;
      }

      const headers = { Authorization: `Bearer ${token}` };

      // Check for in-progress cleanup jobs
      checkInProgressCleanup();
      
      // Load org data
      try {
        const orgRes = await axios.get(`${BACKEND_URL}/api/v1/org`, { headers });
        setOrgData({
          used: orgRes.data.reports_used || 0,
          limit: orgRes.data.report_limit || 25,
          tier: orgRes.data.tier || 'smb'
        });
      } catch (orgError) {
        console.error('Failed to load org data:', orgError);
      }
      
      // Load reports
      try {
        const reportsRes = await axios.get(`${BACKEND_URL}/api/v1/reports`, { headers });
        const reportsData = reportsRes.data.reports || [];
        setReports(reportsData);
        
        // Fetch health scores for completed reports
        const completedReports = reportsData.filter((r) => r.status === 'completed');
        if (completedReports.length > 0) {
          try {
            const hsRes = await axios.get(`${BACKEND_URL}/api/v1/claims/health-scores/summary`, { headers });
            const scoresMap = {};
            (hsRes.data.reports || []).forEach((hs) => {
              scoresMap[hs.report_id] = hs;
            });
            setHealthScores(scoresMap);
          } catch (hsErr) {
            console.error('Failed to load health scores:', hsErr);
          }
        }

        // Fetch collaboration status for all reports
        try {
          const collabRes = await axios.get(`${BACKEND_URL}/api/v1/collaboration/shares/my-reports`, { headers });
          console.log('📊 Raw collaboration response:', collabRes.data);
          
          const collabMap = {};
          // collabRes.data is directly an array of shares
          const shares = collabRes.data || [];
          console.log(`📊 Processing ${shares.length} collaboration shares`);
          
          shares.forEach((share) => {
            console.log(`  - Report ${share.report_id?.substring(0, 8)}: ${share.invite_status} (${share.invite_email})`);
            // Priority: accepted > pending, so only overwrite if current is not accepted
            if (!collabMap[share.report_id] || (share.invite_status === 'accepted' && collabMap[share.report_id].invite_status !== 'accepted')) {
              collabMap[share.report_id] = share;
            }
          });
          
          setCollaborationStatus(collabMap);
          console.log('✅ Collaboration status map:', Object.keys(collabMap).length, 'reports with shares');
          console.log('✅ Full map:', collabMap);
        } catch (collabErr) {
          console.error('❌ Failed to load collaboration status:', collabErr);
          console.error('Error details:', collabErr.response?.data || collabErr.message);
        }
      } catch (reportsError) {
        console.error('Failed to load reports:', reportsError);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('🚀 Dashboard: useEffect running, isLoaded=', isLoaded, 'isSignedIn=', isSignedIn);

    // Small delay to ensure Clerk is ready
    const timer = setTimeout(() => {
      console.log('⏰ Dashboard: Timer fired, calling loadData');
      loadData();
    }, 500);

    // Refresh data every 5 seconds to catch status updates
    const interval = setInterval(() => {
      if (isLoaded && isSignedIn) {
        loadData();
      }
    }, 5000);

    return () => {
      clearTimeout(timer);
      clearInterval(interval);
    };
  }, []);

  // Filter and sort reports
  const filteredAndSortedReports = useMemo(() => {
    let filtered = [...reports];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((r) => 
        r.claimant_name?.toLowerCase().includes(query) ||
        r.client_name?.toLowerCase().includes(query) ||
        r.business_name?.toLowerCase().includes(query) ||
        r.id?.toLowerCase().includes(query) ||
        r.event_type?.toLowerCase().includes(query)
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter((r) => r.status === statusFilter);
    }

    // Health filter
    if (healthFilter !== 'all') {
      filtered = filtered.filter((r) => {
        const health = healthScores[r.id];
        return health?.status === healthFilter;
      });
    }

    // Collaboration filter
    if (collaborationFilter !== 'all') {
      filtered = filtered.filter((r) => {
        const collab = collaborationStatus[r.id];
        if (collaborationFilter === 'not_shared') return !collab;
        if (collaborationFilter === 'pending') return collab?.invite_status === 'pending';
        if (collaborationFilter === 'accepted') return collab?.invite_status === 'accepted';
        return true;
      });
    }

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'date_desc':
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        case 'date_asc':
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        case 'amount_desc':
          return (b.result?.final_claim_amount || 0) - (a.result?.final_claim_amount || 0);
        case 'amount_asc':
          return (a.result?.final_claim_amount || 0) - (b.result?.final_claim_amount || 0);
        case 'name_asc':
          return (a.claimant_name || '').localeCompare(b.claimant_name || '');
        case 'name_desc':
          return (b.claimant_name || '').localeCompare(a.claimant_name || '');
        default:
          return 0;
      }
    });

    return filtered;
  }, [reports, searchQuery, statusFilter, healthFilter, collaborationFilter, sortBy, healthScores, collaborationStatus]);

  // Health score statistics
  const healthStats = useMemo(() => {
    const completed = reports.filter((r) => r.status === 'completed');
    const green = completed.filter((r) => healthScores[r.id]?.status === 'GREEN').length;
    const yellow = completed.filter((r) => healthScores[r.id]?.status === 'YELLOW').length;
    const red = completed.filter((r) => healthScores[r.id]?.status === 'RED').length;
    return { total: reports.length, green, yellow, red };
  }, [reports, healthScores]);

  // Phase 3: Analytics statistics
  const analyticsStats = useMemo(() => {
    const completed = reports.filter((r) => r.status === 'completed');
    const totalClaimValue = completed.reduce((sum, r) => {
      const amount = r.result?.final_claim_amount || 0;
      return sum + (typeof amount === 'number' ? amount : 0);
    }, 0);
    
    const averageClaimValue = completed.length > 0 
      ? Math.round(totalClaimValue / completed.length) 
      : 0;
    
    // Success rate: reports that passed causation (not status='causation_failed')
    const passedCount = completed.filter((r) => r.status !== 'causation_failed').length;
    const failedCount = reports.filter((r) => r.status === 'causation_failed').length;
    const totalProcessed = completed.length + failedCount;
    const successRate = totalProcessed > 0 
      ? Math.round((passedCount / totalProcessed) * 100) 
      : 0;
    
    return {
      totalClaimValue: Math.round(totalClaimValue),
      averageClaimValue,
      completedReports: completed.length,
      successRate,
      passedCount,
      failedCount
    };
  }, [reports]);

  // Phase 3: Recent activity feed
  const recentActivity = useMemo(() => {
    // Helper function to calculate time ago
    const getTimeAgo = (date) => {
      const seconds = Math.floor((new Date() - date) / 1000);
      
      if (seconds < 60) return 'Just now';
      if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
      if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;
      if (seconds < 604800) return `${Math.floor(seconds / 86400)} days ago`;
      return date.toLocaleDateString();
    };

    const activities = [];
    
    // Get most recent reports (last 5)
    const sortedByDate = [...reports].sort((a, b) => 
      new Date(b.created_at || 0) - new Date(a.created_at || 0)
    ).slice(0, 5);
    
    sortedByDate.forEach((report) => {
      const timeAgo = report.created_at 
        ? getTimeAgo(new Date(report.created_at)) 
        : 'Recently';
      
      if (report.status === 'completed') {
        activities.push({
          icon: '✅',
          title: `Report completed for ${report.claimant_name || 'Unknown'}`,
          time: timeAgo
        });
      } else if (report.status === 'causation_failed') {
        activities.push({
          icon: '⚠️',
          title: `Causation test failed for ${report.claimant_name || 'Unknown'}`,
          time: timeAgo
        });
      } else if (report.status === 'processing') {
        activities.push({
          icon: '⏳',
          title: `Processing report for ${report.claimant_name || 'Unknown'}`,
          time: timeAgo
        });
      } else if (report.status === 'draft') {
        activities.push({
          icon: '📝',
          title: `Draft created for ${report.claimant_name || 'Unknown'}`,
          time: timeAgo
        });
      }
    });
    
    return activities;
  }, [reports]);

  // Quick Duplicate Handler
  const handleDuplicate = async (reportId) => {
    try {
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      
      const response = await axios.post(
        `${BACKEND_URL}/api/v1/reports/${reportId}/duplicate`,
        {},
        { headers }
      );
      
      if (response.data.success) {
        navigate(`/wizard-v2?draft_id=${response.data.draft_id}`);
      }
    } catch (error) {
      console.error('Failed to duplicate claim:', error);
      setErrorMessage('Failed to duplicate claim. Please try again.');
    }
  };

  // Delete Report Handler
  const handleDelete = async (reportId) => {
    try {
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      
      const response = await axios.delete(
        `${BACKEND_URL}/api/v1/reports/${reportId}`,
        { headers }
      );
      
      if (response.data.success) {
        console.log('✅ Report deleted successfully');
        
        // Log delete activity
        logActivity({
          reportId,
          actionType: 'delete',
          actionDetails: 'Deleted report',
          section: 'dashboard',
          metadata: {}
        });
        
        await loadData();
      }
    } catch (error) {
      console.error('Failed to delete report:', error);
      setErrorMessage(error.response?.data?.detail || 'Failed to delete report. Please try again.');
    }
  };

  // Share Report Handler
  const handleShare = (reportId) => {
    // TODO: Open share modal (implement in future)
    setStatusMessage(`Share functionality coming soon for report: ${reportId.substring(0, 8)}`);
  };

  // Removed orphaned code block

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        
        {/* Inline status/error messages */}
        {errorMessage && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600 text-sm">{errorMessage}</p>
          </div>
        )}
        {statusMessage && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-green-600 text-sm">{statusMessage}</p>
          </div>
        )}

        {/* Resume Cleanup Banner */}
        {inProgressCleanup && (
          <div className="mb-6 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center animate-pulse">
                  <span className="text-xl">🔄</span>
                </div>
                <div>
                  <h3 className="font-semibold text-amber-900">Resume Cleanup in Progress</h3>
                  <p className="text-sm text-amber-700">
                    You have an unfinished cleanup job ({inProgressCleanup.stats?.reviewed || 0} of {inProgressCleanup.stats?.total_transactions || 0} transactions reviewed)
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right mr-4">
                  <p className="text-xs text-amber-600">Progress</p>
                  <p className="font-bold text-amber-900">{inProgressCleanup.progress_pct || 0}%</p>
                </div>
                <button
                  onClick={() => navigate(`/cleanup/jobs/${inProgressCleanup.id}/validation`)}
                  className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-lg transition-colors flex items-center gap-2"
                >
                  ▶️ Resume
                </button>
                <button
                  onClick={() => setInProgressCleanup(null)}
                  className="p-2 text-amber-400 hover:text-amber-600"
                  title="Dismiss"
                >
                  ✕
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900">ClaimSync Dashboard</h1>
          <div className="flex gap-3 items-center">
            <NotificationBell />
            <button
              onClick={() => navigate('/settings')}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 px-4 rounded-lg transition-colors flex items-center gap-2"
              title="Settings"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </button>
            <button
              onClick={() => signOut()}
              className="bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-3 px-6 rounded-lg transition-colors"
            >
              Sign Out
            </button>
            <button
              onClick={() => navigate('/compare')}
              className="text-white font-semibold py-3 px-6 rounded-lg shadow-lg transition-colors"
              style={{ backgroundColor: 'var(--color-secondary)' }}
            >
              🔍 Compare Reports
            </button>
            <button
              onClick={() => navigate('/bulk-upload')}
              className="text-white font-semibold py-3 px-6 rounded-lg shadow-lg transition-colors flex items-center gap-2"
              style={{ backgroundColor: 'var(--color-primary)' }}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              📦 Bulk Upload / New Report
            </button>
          </div>
          
          {/* Advanced Tools - De-emphasized */}
          <div className="mt-2 text-right">
            <button
              onClick={() => navigate('/cleanup')}
              className="text-sm text-gray-500 hover:text-blue-600 transition-colors inline-flex items-center gap-1"
            >
              🧹 Advanced: Run BI Cleanup Manually
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>

        {/* Quota Meter */}
        <div className="mb-8">
          <QuotaMeter used={orgData.used} limit={orgData.limit} tier={orgData.tier} />
        </div>

        {/* Health Score Dashboard Widget */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div 
            className="bg-white rounded-xl shadow-lg p-6 cursor-pointer hover:shadow-xl transition"
            onClick={() => { setStatusFilter('all'); setHealthFilter('all'); }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Reports</p>
                <p className="text-3xl font-bold text-gray-900">{healthStats.total}</p>
              </div>
              <span className="text-4xl">📊</span>
            </div>
          </div>

          <div 
            className="bg-white rounded-xl shadow-lg p-6 cursor-pointer hover:shadow-xl transition"
            onClick={() => { setHealthFilter('GREEN'); }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Ready</p>
                <p className="text-3xl font-bold text-green-600">{healthStats.green}</p>
              </div>
              <span className="text-4xl">🟢</span>
            </div>
          </div>

          <div 
            className="bg-white rounded-xl shadow-lg p-6 cursor-pointer hover:shadow-xl transition"
            onClick={() => { setHealthFilter('YELLOW'); }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Review</p>
                <p className="text-3xl font-bold text-yellow-600">{healthStats.yellow}</p>
              </div>
              <span className="text-4xl">🟡</span>
            </div>
          </div>

          <div 
            className="bg-white rounded-xl shadow-lg p-6 cursor-pointer hover:shadow-xl transition"
            onClick={() => { setHealthFilter('RED'); }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Issues Found</p>
                <p className="text-3xl font-bold text-red-600">{healthStats.red}</p>
              </div>
              <span className="text-4xl">🔴</span>
            </div>
          </div>
        </div>

        {/* Phase 3: Analytics & Insights Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Financial Analytics */}
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl shadow-lg p-6">
            <h3 className="text-lg font-bold text-blue-900 mb-4 flex items-center gap-2">
              <span>💰</span> Financial Overview
            </h3>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-blue-700 mb-1">Total Claim Value</p>
                <p className="text-2xl font-bold text-blue-900">
                  {formatCurrency(analyticsStats.totalClaimValue)}
                </p>
              </div>
              <div>
                <p className="text-sm text-blue-700 mb-1">Average Claim</p>
                <p className="text-2xl font-bold text-blue-900">
                  {formatCurrency(analyticsStats.averageClaimValue)}
                </p>
              </div>
              <div className="pt-2 border-t border-blue-200">
                <p className="text-xs text-blue-600">
                  Based on {analyticsStats.completedReports} completed reports
                </p>
              </div>
            </div>
          </div>

          {/* Success Rate */}
          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl shadow-lg p-6">
            <h3 className="text-lg font-bold text-green-900 mb-4 flex items-center gap-2">
              <span>📈</span> Success Metrics
            </h3>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-green-700 mb-1">Causation Success Rate</p>
                <p className="text-4xl font-bold text-green-900">
                  {analyticsStats.successRate}%
                </p>
              </div>
              <div className="grid grid-cols-2 gap-2 pt-2 border-t border-green-200">
                <div>
                  <p className="text-xs text-green-600">Passed</p>
                  <p className="text-lg font-bold text-green-800">{analyticsStats.passedCount}</p>
                </div>
                <div>
                  <p className="text-xs text-green-600">Failed</p>
                  <p className="text-lg font-bold text-red-600">{analyticsStats.failedCount}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Recent Activity Feed */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <span>🕒</span> Recent Activity
            </h3>
            <div className="space-y-3 max-h-48 overflow-y-auto">
              {recentActivity.length > 0 ? (
                recentActivity.map((activity, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-2 hover:bg-gray-50 rounded-lg">
                    <span className="text-xl">{activity.icon}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {activity.title}
                      </p>
                      <p className="text-xs text-gray-500">{activity.time}</p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">No recent activity</p>
              )}
            </div>
          </div>
        </div>

        {/* Saved Views & Templates Bar */}
        <div className="bg-gradient-to-r from-blue-50 to-blue-50 rounded-xl shadow-sm p-4 mb-4">
          <div className="flex flex-wrap gap-3 items-center">
            <SavedViewsManager
              currentFilters={{ status: statusFilter, healthFilter, collaborationFilter, searchQuery, tagFilter }}
              currentSort={{ sortBy: sortBy.split('_')[0], sortOrder: sortBy.split('_')[1] }}
              onApplyView={handleApplySavedView}
            />
            <ReportTemplatesManager onSelectTemplate={handleSelectTemplate} />
          </div>
        </div>

        {/* Search & Filter Bar */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {/* Search */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
              <input
                type="text"
                placeholder="Search by claimant, business, report ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="completed">Completed</option>
                <option value="draft">Draft</option>
                <option value="processing">Processing</option>
                <option value="causation_failed">Causation Failed</option>
              </select>
            </div>

            {/* Collaboration Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Collaboration</label>
              <select
                value={collaborationFilter}
                onChange={(e) => setCollaborationFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Reports</option>
                <option value="not_shared">Not Shared</option>
                <option value="pending">Invited</option>
                <option value="accepted">Accepted</option>
              </select>
            </div>

            {/* Sort */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="date_desc">Date (Newest)</option>
                <option value="date_asc">Date (Oldest)</option>
                <option value="amount_desc">Claim Amount (High to Low)</option>
                <option value="amount_asc">Claim Amount (Low to High)</option>
                <option value="name_asc">Claimant (A-Z)</option>
                <option value="name_desc">Claimant (Z-A)</option>
              </select>
            </div>
          </div>

          {/* Active Filters Display */}
          {(searchQuery || statusFilter !== 'all' || healthFilter !== 'all' || collaborationFilter !== 'all') && (
            <div className="mt-4 flex items-center gap-2 flex-wrap">
              <span className="text-sm text-gray-600">Active filters:</span>
              {searchQuery && (
                <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">
                  Search: &quot;{searchQuery}&quot;
                  <button onClick={() => setSearchQuery('')} className="ml-1 hover:text-blue-900">×</button>
                </span>
              )}
              {statusFilter !== 'all' && (
                <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">
                  Status: {statusFilter}
                  <button onClick={() => setStatusFilter('all')} className="ml-1 hover:text-blue-900">×</button>
                </span>
              )}
              {healthFilter !== 'all' && (
                <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm">
                  Health: {healthFilter}
                  <button onClick={() => setHealthFilter('all')} className="ml-1 hover:text-green-900">×</button>
                </span>
              )}
              {collaborationFilter !== 'all' && (
                <span className="inline-flex items-center gap-1 bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-sm">
                  Collaboration: {collaborationFilter.replace('_', ' ')}
                  <button onClick={() => setCollaborationFilter('all')} className="ml-1 hover:text-orange-900">×</button>
                </span>
              )}
              <button 
                onClick={() => { setSearchQuery(''); setStatusFilter('all'); setHealthFilter('all'); setCollaborationFilter('all'); }}
                className="text-sm text-red-600 hover:text-red-800 font-medium"
              >
                Clear all
              </button>
            </div>
          )}
        </div>

        {/* Reports Grid */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                Your Reports ({filteredAndSortedReports.length})
              </h2>
              {bulkMode && selectedReports.size > 0 && (
                <p className="text-sm text-blue-600 mt-1">
                  {selectedReports.size} report(s) selected
                </p>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2">
              {/* Export CSV Button */}
              <button
                onClick={handleExportCSV}
                className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Export CSV
              </button>

              {/* Bulk Actions Toggle */}
              <button
                onClick={() => {
                  setBulkMode(!bulkMode);
                  setSelectedReports(new Set());
                }}
                className={`inline-flex items-center gap-2 px-4 py-2 font-semibold rounded-lg transition ${
                  bulkMode 
                    ? 'bg-blue-600 text-white hover:bg-blue-700' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
                {bulkMode ? 'Exit Bulk Mode' : 'Bulk Select'}
              </button>
            </div>
          </div>

          {/* Bulk Action Bar */}
          {bulkMode && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4 flex justify-between items-center">
              <div className="flex items-center gap-4">
                <button
                  onClick={handleSelectAll}
                  className="text-blue-700 hover:text-blue-900 font-medium text-sm"
                >
                  {selectedReports.size === filteredAndSortedReports.length ? 'Deselect All' : 'Select All'}
                </button>
                <span className="text-sm text-blue-600">
                  {selectedReports.size} of {filteredAndSortedReports.length} selected
                </span>
              </div>

              {selectedReports.size > 0 && (
                <div className="flex gap-2">
                  <button
                    onClick={handleExportCSV}
                    className="inline-flex items-center gap-1 px-3 py-2 bg-green-100 hover:bg-green-200 text-green-700 font-medium rounded-lg transition text-sm"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Export Selected
                  </button>
                  <button
                    onClick={handleBulkDelete}
                    className="inline-flex items-center gap-1 px-3 py-2 bg-red-100 hover:bg-red-200 text-red-700 font-medium rounded-lg transition text-sm"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Delete Selected
                  </button>
                </div>
              )}
            </div>
          )}

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading reports...</p>
            </div>
          ) : filteredAndSortedReports.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600 mb-4">
                {reports.length === 0 ? 'No reports yet. Create your first report!' : 'No reports match your filters.'}
              </p>
              {reports.length > 0 && (
                <button
                  onClick={() => { setSearchQuery(''); setStatusFilter('all'); setHealthFilter('all'); setCollaborationFilter('all'); }}
                  className="text-blue-600 hover:text-blue-800 font-medium"
                >
                  Clear filters
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {filteredAndSortedReports.map((report) => (
                <ReportCard
                  key={report.id}
                  report={report}
                  healthScore={healthScores[report.id]}
                  collaborationStatus={collaborationStatus[report.id]}
                  onDuplicate={handleDuplicate}
                  onDelete={handleDelete}
                  onShare={handleShare}
                  selected={selectedReports.has(report.id)}
                  onSelect={handleSelectReport}
                  bulkMode={bulkMode}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
